/**
 * Fallback static page data for legal and policy CMS pages.
 *
 * When pages like /p/cancellation-policy or /p/refund-policy are requested,
 * this module supplies pre-formatted PageBuilder section data if the corresponding
 * JSON asset has not been uploaded to Sharetribe Console Asset Delivery API.
 */

const cancellationPolicyEn = `
# Arabibi Cancellation Policy

*Last updated: November 2026*

At Arabibi, we strive to provide a flexible and reliable learning experience for both students and tutors. Because tutors set aside dedicated time slots for your lessons, we operate a fair cancellation policy.

---

## 1. Student Cancellations

### Early Cancellation (12+ Hours Notice)
* If you cancel a lesson **at least 12 hours before** the scheduled start time, you are eligible for a **100% full refund**.
* The refund is automatically credited back to your **Arabibi Wallet** immediately upon cancellation, allowing you to rebook whenever you are ready.

### Late Cancellation (Less Than 12 Hours Notice)
* If you cancel a lesson **less than 12 hours before** the scheduled start time, the booking is non-refundable.
* The lesson credit will be consumed and the tutor will receive compensation for the reserved time slot.

---

## 2. Tutor Cancellations
* If your tutor is unable to conduct a scheduled lesson and cancels at any time, you will receive an **immediate 100% refund** to your Arabibi Wallet.
* Tutors who cancel with less than 12 hours notice receive an internal platform warning to ensure high teaching reliability across our marketplace.

---

## 3. Free Trial Lessons
* If you cancel a free trial lesson, the trial credit is returned to your account so you can reschedule with another tutor or at a different time.

---

## 4. Rescheduling Lessons
* You can reschedule an upcoming session through the platform ahead of time without penalty. If rescheduling within the late window, tutor approval or platform guidelines apply.

---

## 5. Contacting Support
If you experienced an emergency, medical issue, or technical problem that prevented you from cancelling on time, please contact our support team at **support@arabibi.com** or **admin@arabibi.com** for assistance.
`;

const cancellationPolicyAr = `
# سياسة الإلغاء في عربـيبي

*آخر تحديث: نوفمبر 2026*

نحرص في عربـيبي على توفير تجربة تعليمية مريحة ومرنة وموثوقة لكل من الطلاب والمعلمين. ولأن معلّمينا يخصصون أوقاتهم الحصرية لدروسكم، فإننا نتبع سياسة إلغاء متوازنة وعادلة.

---

## 1. إلغاء الحصص من قِبل الطالب

### الإلغاء المبكر (قبل 12 ساعة أو أكثر)
* إذا قمت بإلغاء الحصة **قبل 12 ساعة على الأقل** من موعد بدئها المقرر، يحق لك استرداد كامل المبلغ بنسبة **100%**.
* يتم إيداع المبلغ المسترد تلقائيًا وبشكل فوري في **محفظة عربـيبي** الخاصة بك لتتمكن من حجز موعد جديد في أي وقت يناسبك.

### الإلغاء المتأخر (أقل من 12 ساعة)
* إذا تم الإلغاء **قبل أقل من 12 ساعة** من موعد الحصة، فإن الحصة غير قابلة للاسترداد.
* يُعتبر رصيد الحصة مستهلكاً ويتم تعويض المعلم عن الوقت المحجوز.

---

## 2. إلغاء الحصص من قِبل المعلم
* إذا اضطر المعلم إلى إلغاء الحصة المجدولة في أي وقت، يسترد الطالب **كامل المبلغ بنسبة 100%** فوريًا إلى محفظته في عربـيبي.
* المعلم الذي يلغي قبل أقل من 12 ساعة يُسجل بحقه تنبيه على المنصة لضمان جودة الالتزام والخدمة.

---

## 3. الحصص التجريبية المجانية
* عند إلغاء حصة تجريبية مجانية، يُعاد رصيد التجربة المجانية إلى حسابك لتتمكن من تجربته مرة أخرى مع معلم آخر أو في وقت مختلف.

---

## 4. إعادة الجدولة
* يمكنك إعادة جدولة الحصص القادمة عبر المنصة مسبقاً دون أي رسوم إضافية وفقاً للمواعيد المتاحة للمعلم.

---

## 5. التواصل مع فريق الدعم
في حال حدوث ظرف طارئ أو عطل تقني حال دون الحضور أو الإلغاء في الوقت المحدد، يُرجى التواصل مع فريق الدعم عبر **support@arabibi.com** أو **admin@arabibi.com**.
`;

const refundPolicyEn = `
# Arabibi Refund Policy

*Last updated: November 2026*

Arabibi is committed to providing a transparent, fair, and secure payment environment for all learners and tutors. This policy outlines how refunds, wallet balances, and transaction adjustments are processed.

---

## 1. Arabibi Wallet & Credits
* **No Expiration:** Funds deposited into your Arabibi Wallet never expire. You can utilize your wallet balance at your own pace for single lessons, subscriptions, or packages.
* **Instant Wallet Credits:** Refunds originating from early cancellations, tutor cancellations, or dispute resolutions are deposited directly into your Arabibi Wallet instantly.

---

## 2. Eligible Refund Circumstances

You are eligible for a full refund to your wallet in the following situations:
1. **Early Lesson Cancellations:** Cancelling any scheduled session with **12 or more hours notice** prior to start time.
2. **Tutor Cancellations:** Any session cancelled by the tutor.
3. **Tutor No-Shows:** If a tutor fails to attend a confirmed lesson within 10 minutes of the start time and the lesson cannot proceed.
4. **Verified Technical Issues:** Platform-side audio/video disruptions or verified connectivity failures that prevent the lesson from taking place.
5. **Approved Dispute Resolutions:** Valid disputes reported within 48 hours of session completion through the Arabibi dispute system.

---

## 3. Non-Refundable Situations

Refunds cannot be granted in the following scenarios:
* **Late Cancellations:** Cancellations initiated by the student less than 12 hours before the session.
* **Student No-Shows:** Failure of the student to join the session without prior notice or cancellation.
* **Completed Lessons:** Lessons successfully attended and completed without timely dispute or reported issue.

---

## 4. Reversal to Original Payment Method
* By default, all refunds are credited directly to your Arabibi Wallet for fast, fee-free reuse.
* If you specifically require a refund back to your original payment card (Visa, Mastercard, Apple Pay, Tap, or Stripe), please submit a request to **admin@arabibi.com** with your transaction details. Gateway reversals may take 5–10 business days depending on your bank's processing cycle.

---

## 5. Contact Us
For any billing questions or refund inquiries, please email our finance and support team at **admin@arabibi.com**.
`;

const refundPolicyAr = `
# سياسة الاسترداد في عربـيبي

*آخر تحديث: نوفمبر 2026*

تلتزم منصة عربـيبي بتوفير بيئة دفع تتسم بالشفافية والعدالة والأمان لجميع المتعلمين والمعلمين. توضح هذه السياسة آلية الاسترداد ورصيد المحفظة وتسوية المعاملات المالية.

---

## 1. محفظة عربـيبي والرصيد
* **صلاحية دائمة:** الرصيد المودع في محفظة عربـيبي لا تنتهي صلاحيته أبدًا، ويمكنك استخدامه في أي وقت لشراء الدروس الفردية أو الاشتراكات أو باقات الدروس.
* **إيداع فوري:** يتم إيداع المبالغ المستردة الناتجة عن الإلغاء المبكر أو إلغاء المعلم أو تسوية النزاعات مباشرة وبشكل فوري في محفظتك.

---

## 2. الحالات المؤهلة للاسترداد

يحق للطالب استرداد كامل المبلغ إلى محفظته في الحالات التالية:
1. **الإلغاء المبكر للدرس:** عند إلغاء الحصة قبل **12 ساعة أو أكثر** من موعد بدئها.
2. **إلغاء المعلم:** أي حصة يتم إلغاؤها من قِبل المعلم.
3. **عدم حضور المعلم:** إذا تغيب المعلم عن الحصة المؤكدة لأكثر من 10 دقائق من موعد البدء.
4. **مشكلات فنية موثقة:** حدوث أعطال تقنية من طرف المنصة تحول دون إتمام الدرس.
5. **تسوية النزاعات المقبولة:** البلاغات والنزاعات المقدمة خلال 48 ساعة من موعد الدرس والتي تقر إدارة المنصة أحقيتها.

---

## 3. الحالات غير القابلة للاسترداد

لا يمكن استرداد المبالغ في الحالات الآتية:
* **الإلغاء المتأخر:** إلغاء الحصة من قِبل الطالب قبل أقل من 12 ساعة من موعدها.
* **تغيب الطالب:** عدم حضور الطالب للدرس دون تقديم طلب إلغاء مسبق.
* **الدروس المكتملة:** الحصص التي تم تقديمها وحضورها بنجاح دون أي نزاع في حينه.

---

## 4. الاسترداد إلى وسيلة الدفع الأصلية
* كإجراء افتراضي وسريع، تودع المبالغ المستردة في محفظة عربـيبي دون خصم أي رسوم مصرفية.
* في حال رغبتكم في استرداد المبلغ إلى بطاقة الدفع الأصلية (فيزا، ماستركارد، Apple Pay، تاب، أو سترايب)، يُرجى مراسلة **admin@arabibi.com** مع بيانات المعاملة. تستغرق عمليات الاسترداد البنكي عادة من 5 إلى 10 أيام عمل وفقًا لسياسات البنك المصدر.

---

## 5. التواصل معنا
لأي استفسارات بخصوص الفواتير والمدفوعات والاسترداد، يُرجى مراسلتنا عبر **admin@arabibi.com**.
`;

/**
 * Build a structured PageBuilder asset object matching Sharetribe's JSON format.
 */
const createPolicySection = (id, title, markdownContent) => {
  return {
    sections: [
      {
        sectionType: 'article',
        sectionId: id,
        appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
        title: { fieldType: 'heading1', content: title },
        blocks: [
          {
            blockType: 'defaultBlock',
            blockId: `${id}-content`,
            text: {
              fieldType: 'markdown',
              content: markdownContent,
            },
          },
        ],
      },
    ],
    meta: {
      pageTitle: {
        fieldType: 'metaTitle',
        content: title,
      },
      pageDescription: {
        fieldType: 'metaDescription',
        content: title,
      },
    },
  };
};

export const FALLBACK_POLICIES = {
  'cancellation-policy': createPolicySection(
    'cancellation-policy',
    'Cancellation Policy | Arabibi',
    cancellationPolicyEn
  ),
  'ar-cancellation-policy': createPolicySection(
    'ar-cancellation-policy',
    'سياسة الإلغاء | عربـيبي',
    cancellationPolicyAr
  ),
  'refund-policy': createPolicySection(
    'refund-policy',
    'Refund Policy | Arabibi',
    refundPolicyEn
  ),
  'ar-refund-policy': createPolicySection(
    'ar-refund-policy',
    'سياسة الاسترداد | عربـيبي',
    refundPolicyAr
  ),
};
